import { useContext } from 'react'
import { useParams } from 'react-router-dom'

import { ToastContext } from '@reveni-io/reveni-ui'

import SaveChangesButtons from '@/common/components/SaveChangesButtons/SaveChangesButtons'
import UnsavedChangesAlert from '@/common/components/UnsavedChangesAlert/UnsavedChangesAlert'
import { userPermissions } from '@/common/constants'
import AppContext from '@/common/context/app.context'
import useRegions from '@/common/hooks/useRegions/useRegions'
import useReturnReasons from '@/common/hooks/useReturnReasons/useReturnReasons'
import useRuleFeatures from '@/common/hooks/useRuleFeatures/useRuleFeatures'
import useStoreCurrencySymbol from '@/common/hooks/useStoreCurrencySymbol/useStoreCurrencySymbol'
import useStoreProductCategories from '@/common/hooks/useStoreProductCategories/useStoreProductCategories'
import useStoreProductCollections from '@/common/hooks/useStoreProductCollections/useStoreProductCollections'
import useStoreProductTypes from '@/common/hooks/useStoreProductTypes/useStoreProductTypes'
import useUserPermissions from '@/common/hooks/useUserPermissions/useUserPermissions'
import { focusOnError } from '@/configuration/pages/ConfigurationPage/helpers'

import useOrderTrackingPreview from '../hooks/useOrderTrackingPreview/useOrderTrackingPreview'
import useVisibilityCriterion from './hooks/useVisibilityCriterion/useVisibilityCriterion'
import VisibilityCriterionBasicInfo from './VisibilityCriterionBasicInfo/VisibilityCriterionBasicInfo'
import VisibilityCriterionConditionsEditor from './VisibilityCriterionConditionsEditor/VisibilityCriterionConditionsEditor'
import VisibilityCriterionDetailSkeleton from './VisibilityCriterionDetailSkeleton/VisibilityCriterionDetailSkeleton'
import VisibilityCriterionUsedIn from './VisibilityCriterionUsedIn/VisibilityCriterionUsedIn'
import { validateVisibilityCriterion } from './helpers'
import * as Styles from './styles'

const VisibilityCriterionDetailConfiguration = () => {
  const { id } = useParams()
  const { info } = useContext(AppContext)
  const { setToastInfo } = useContext(ToastContext)
  const { hasPermission } = useUserPermissions()

  const isEditable = hasPermission(userPermissions.editOrderTrackingPage)
  const storeId = info?.selectedStore?.id

  const { disabledConditionFields, fetchingRuleFeatures } = useRuleFeatures({ storeId })
  const { productTypes } = useStoreProductTypes({ storeId, doInitialRequest: true })
  const { productCategories } = useStoreProductCategories({ storeId, doInitialRequest: true })
  const { productCollections } = useStoreProductCollections({ storeId, doInitialRequest: true })
  const { regions } = useRegions()
  const { returnReasons } = useReturnReasons({
    returnPortalId: info?.selectedReturnPortal?.id,
    doInitialRequest: true,
    disabled: false,
  })
  const { currencies, storeCurrencyCode } = useStoreCurrencySymbol()
  const { orderTrackingPreview } = useOrderTrackingPreview({ storeId })

  const {
    criterion,
    fetchingVisibilityCriterion,
    saving,
    hasChanges,
    errors,
    setErrors,
    onChangeCriterion,
    revertChanges,
    saveChanges,
  } = useVisibilityCriterion({ id, storeId, setToastInfo, storeCurrencyCode })

  const handleSave = () => {
    const validationErrors = validateVisibilityCriterion(criterion)

    if (validationErrors.length) {
      focusOnError({ [validationErrors[0].type]: validationErrors[0].type })
      setErrors(validationErrors)

      return
    }

    saveChanges()
  }

  if (fetchingVisibilityCriterion || fetchingRuleFeatures) return <VisibilityCriterionDetailSkeleton />

  return (
    <Styles.MainContainer>
      {hasChanges && <UnsavedChangesAlert hasPermission={isEditable} />}
      <VisibilityCriterionBasicInfo
        name={criterion.name}
        description={criterion.description}
        onChange={onChangeCriterion}
        errors={errors}
        disabled={!isEditable}
      />
      <VisibilityCriterionConditionsEditor
        conditions={criterion.conditions}
        onChange={onChangeCriterion}
        disabled={!isEditable}
        productTypes={productTypes}
        productCategories={productCategories}
        productCollections={productCollections}
        returnReasons={returnReasons}
        regions={regions}
        errors={errors}
        currencies={currencies}
        storeCurrencyCode={storeCurrencyCode}
        disabledConditionFields={disabledConditionFields}
      />
      <VisibilityCriterionUsedIn blockUids={criterion.block_uids} layout={orderTrackingPreview?.layout} />
      <SaveChangesButtons
        loading={saving}
        disabled={saving}
        onSave={handleSave}
        hasChanges={isEditable && hasChanges}
        onRevertChanges={revertChanges}
      />
    </Styles.MainContainer>
  )
}

export default VisibilityCriterionDetailConfiguration
